import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { CommsQuery } from 'src/app/shared/comms/comms-wire.model';
import {
  COMMS_DEMO_ENABLED,
  rememberCommsWrite,
  seedCommunicationThread,
  seedCommunications,
} from './comms-demo-backend';
import { INVENTED_DATA_ENABLED } from './invented-data';

/**
 * Turns a failed `/communications` request into an invented answer.
 *
 * The reasoning and every seed live in {@link ./comms-demo-backend.ts}; this file is only the
 * wiring. It does one thing: the real request goes out, and **only if the server cannot answer it
 * at all** is a stand-in substituted.
 *
 * **To remove it:** delete this file, `comms-demo-backend.ts`, and the word
 * `commsDemoBackendInterceptor` from the `withInterceptors([...])` list in `app.config.ts`.
 */

/**
 * The only paths this may touch.
 *
 * ⚠ Nothing else in this app calls `/communications`. That is what makes endpoint-scoping safe
 * here where it was not safe for the directory stand-in: this cannot reach another component
 * because no other component asks for this path.
 */
function classify(req: HttpRequest<unknown>): 'list' | 'thread' | 'write' | null {
  let path: string;
  try {
    path = new URL(req.url, window.location.origin).pathname;
  } catch {
    path = req.url.split('?')[0];
  }

  // ⚠ Match the SEGMENT, not the prefix. `indexOf('/communications')` alone also matches
  // `/project/7/communications-report`, which belongs to somebody else — the same trap the
  // directory stand-in documents about `/directories` and `/directories_old`. Caught by a spec.
  const at = path.search(/\/communications(?=$|[/?#])/);
  if (at === -1) return null;
  const rest = path.slice(at + '/communications'.length);

  if (rest === '' || rest === '/') return req.method === 'GET' ? 'list' : 'write';
  if (/^\/[^/]+$/.test(rest)) return req.method === 'GET' ? 'thread' : 'write';
  return 'write';
}

/** Which action a write path stands for, so the stand-in can remember it. */
function writeKind(url: string): 'read' | 'unread' | 'archive' | 'unarchive' | 'follow' | 'unfollow' | null {
  const match = url.match(/\/communications\/[^/?#]+\/(read|unread|archive|unarchive|follow|unfollow)\b/);
  return (match?.[1] as ReturnType<typeof writeKind>) ?? null;
}

function threadIdIn(url: string): string {
  return url.match(/\/communications\/([^/?#]+)/)?.[1] ?? '';
}

/** The query the caller sent, as the seed reads it. */
function queryOf(req: HttpRequest<unknown>): CommsQuery {
  const out: Record<string, unknown> = {};
  for (const key of req.params.keys()) out[key] = req.params.get(key);
  return out as CommsQuery;
}

/**
 * Which failures mean "this endpoint does not exist".
 *
 * **404** is the server saying so. **0** is no answer at all — no API running locally, which is the
 * normal state while reviewing this work. Everything else (401, 403, 429, 500) comes from an
 * endpoint that *does* exist, and swallowing it would hide a real fault.
 */
function missing(error: HttpErrorResponse): boolean {
  return error.status === 404 || error.status === 0;
}

export const commsDemoBackendInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  // Every conversation this layer answers with is invented, so with real data only the real
  // failure reaches the page and it shows its empty or error state.
  if (!COMMS_DEMO_ENABLED || !INVENTED_DATA_ENABLED) return next(req);

  const kind = classify(req);
  if (!kind) return next(req);

  // The real request always goes out first. The day it succeeds, nothing below runs.
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!missing(error)) return throwError(() => error);

      // ⚠ The seeds arrive as a promise: they live in a lazily-imported module so the Inbox's
      // cast stays out of the initial bundle. See comms-demo-backend.seedCommunications.
      if (kind === 'list') {
        return from(seedCommunications(queryOf(req))).pipe(
          map(body => new HttpResponse({ status: 200, body, url: req.url })),
        );
      }

      if (kind === 'thread') {
        return from(seedCommunicationThread(threadIdIn(req.url))).pipe(
          switchMap(thread => thread
            ? of(new HttpResponse({ status: 200, body: thread, url: req.url }))
            : throwError(() => error)),
        );
      }

      // A write. Remembered locally so the control the reader pressed visibly did something,
      // then answered 204 as the real endpoint would. Nothing is sent anywhere.
      const action = writeKind(req.url);
      if (action) rememberCommsWrite(threadIdIn(req.url), action);
      return of(new HttpResponse({ status: 204, body: null, url: req.url }));
    }),
  );
};
